import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createBusinessesApi } from "../api";
import type { BusinessDto } from "../types";
import { answerLabel } from "../labels";
import { useLiveRefresh } from "@/lib/useLiveRefresh";

/**
 * Applications that were cancelled, kept for the contact details on them.
 *
 * A cancelled application is a lead. Somebody typed a venue name, an email and
 * a phone number and then stopped — that is worth keeping, and it is not
 * visible anywhere else once the business leaves the active directory.
 *
 * ⚠️ Only applications that actually carry answers appear here. Cancelling an
 * empty form leaves nothing worth storing, and Play deletes those outright
 * rather than filling this list with blank rows — see `cancelApplication` in
 * Play's lib/business.ts.
 */
export function CancelledApplicationsPage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createBusinessesApi(client), [client]);

  usePageBreadcrumbs([
    { label: "Business Center", to: "/app/businesses" },
    { label: "Cancelled Applications" },
  ]);

  const [rows, setRows] = useState<BusinessDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const response = await api.list({
        status: "closed",
        sort: "-created_at",
        limit: 100,
      });
      // A lead is a row with something on it. An empty cancellation is noise.
      setRows(
        response.businesses.filter(
          (b) =>
            b.application_answers &&
            Object.keys(b.application_answers).length > 0,
        ),
      );
    } catch (err) {
      setError(apiErrorMessage(err));
      if (!opts?.silent) setRows([]);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  useLiveRefresh(() => void load({ silent: true }));

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Cancelled Applications"
        description="Applications withdrawn before approval, kept for the contact details on them. Empty cancellations are not stored."
        actions={
          <Flex gap={8}>
            <Button
              variant="secondary"
              onClick={() => void navigate("/app/businesses")}
            >
              Business Center
            </Button>
            <Button variant="secondary" onClick={() => void load()}>
              Refresh
            </Button>
          </Flex>
        }
      />

      {error ? (
        <Alert tone="danger" title="Unable to load cancelled applications">
          {error}
        </Alert>
      ) : null}

      {loading ? <LoadingState message="Loading cancelled applications" /> : null}

      {!loading && rows.length === 0 && !error ? (
        <EmptyState
          title="No cancelled applications"
          description="An application that is cancelled with details filled in will appear here."
        />
      ) : null}

      {rows.map((business) => (
        <Card
          key={business.id}
          header={
            <Flex justify="space-between" align="center" wrap gap={8}>
              <Text role="h2" as="h2">
                {business.display_name}
              </Text>
              <Flex gap={8} align="center">
                <StatusChip tone="neutral">cancelled</StatusChip>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void navigate(`/app/businesses/${business.id}`)}
                >
                  Open
                </Button>
              </Flex>
            </Flex>
          }
        >
          <Stack gap={8}>
            <Text role="caption" color="muted" as="p">
              Applied {new Date(business.created_at).toLocaleString()} · Public ID{" "}
              {business.public_id}
            </Text>
            {business.review_note ? (
              <Text role="small" color="secondary" as="p">
                Reason: {business.review_note}
              </Text>
            ) : null}
            <Table>
              <Thead>
                <Tr>
                  <Th>Field</Th>
                  <Th>Value</Th>
                </Tr>
              </Thead>
              <Tbody>
                {Object.entries(business.application_answers ?? {}).map(
                  ([key, value]) => (
                    <Tr key={key}>
                      <Td>{answerLabel(key)}</Td>
                      <Td>{value}</Td>
                    </Tr>
                  ),
                )}
              </Tbody>
            </Table>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
